import {
  Component,
  ChangeDetectionStrategy,
  signal,
  computed,
  inject,
  OnInit,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { form, max, min, required, submit } from '@angular/forms/signals';
import { GeocodingMap } from './shared/geocoding-map/geocoding-map';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import {
  AutoCompleteModule,
  AutoCompleteCompleteEvent,
  AutoCompleteSelectEvent,
} from 'primeng/autocomplete';
import {
  GeocodingSettingsApi,
  GeocodingFocusPoint,
  UpdateGeocodingSettings,
} from '@tourfold/libraries/tf-api-client-public-v2';
import {
  GeocodingV2Api,
  SuggestedAddressV2Response,
} from '@tourfold/libraries/tf-api-client-frontend-v2';
import { from, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import {
  resolveSettingsLoadErrorDetails,
  SettingsActions,
  SettingsInlineMessage,
  SettingsLoadError,
  type SettingsLoadErrorDetails,
} from '@tourfold/features/settings-ui';
import { ResourceAccessService } from '@tourfold/shared-kernels/resource-access';
import { SignalFormServerErrors } from '@tourfold/libraries/shared-util';

interface CountryOption {
  label: string;
  value: string;
}

interface SuggestionOption {
  label: string;
  address: SuggestedAddressV2Response;
}

interface GeocodingFormData {
  defaultCountry: string;
  defaultFocusPointLat: number | null;
  defaultFocusPointLon: number | null;
}

const EMPTY_FORM: GeocodingFormData = {
  defaultCountry: '',
  defaultFocusPointLat: null,
  defaultFocusPointLon: null,
};

@Component({
  selector: 'tf-settings-page-geocoding',
  imports: [
    CommonModule,
    FormsModule,
    GeocodingMap,
    TranslateModule,
    InputTextModule,
    SelectModule,
    ButtonModule,
    AutoCompleteModule,
    SettingsActions,
    SettingsInlineMessage,
    SettingsLoadError,
  ],
  templateUrl: './settings-geocoding.component.html',
  styleUrl: './settings-geocoding.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GeocodingSettingsComponent implements OnInit {
  private readonly geocodingSettingsApi = inject(GeocodingSettingsApi);
  private readonly geocodingV2Api = inject(GeocodingV2Api);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly resourceAccessService = inject(ResourceAccessService);

  readonly canUpdateGeocoding = computed(() =>
    this.resourceAccessService.canPerform('geocodingSettings', 'update')
  );

  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly hasLoadedOnce = signal(false);
  readonly initialLoadFailed = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly loadErrorDetails = signal<SettingsLoadErrorDetails | null>(null);
  readonly saveError = signal<string | null>(null);
  // Toggled to true on the first save attempt so client-side validation messages appear
  // for fields the user never focused (e.g. lat/lon set entirely via the map).
  readonly submitted = signal(false);

  readonly formModel = signal<GeocodingFormData>(EMPTY_FORM);

  /**
   * Country value bound to the PrimeNG select. Empty string is coerced to `null` so the control
   * reads as "nothing selected" — otherwise PrimeNG treats `''` as filled and renders the clear
   * (X) icon in the initial empty state (TF-1925).
   */
  readonly selectedCountry = computed<string | null>(() => {
    const country = this.formModel().defaultCountry;
    return country === '' ? null : country;
  });

  readonly settingsForm = form(this.formModel, (schema) => {
    required(schema.defaultCountry, { message: 'SETTINGS_GEOCODING.DEFAULT_COUNTRY_REQUIRED' });
    required(schema.defaultFocusPointLat, { message: 'SETTINGS_GEOCODING.LATITUDE_REQUIRED' });
    required(schema.defaultFocusPointLon, { message: 'SETTINGS_GEOCODING.LONGITUDE_REQUIRED' });
    min(schema.defaultFocusPointLat, -90, { message: 'SETTINGS_GEOCODING.LATITUDE_INVALID' });
    max(schema.defaultFocusPointLat, 90, { message: 'SETTINGS_GEOCODING.LATITUDE_INVALID' });
    min(schema.defaultFocusPointLon, -180, { message: 'SETTINGS_GEOCODING.LONGITUDE_INVALID' });
    max(schema.defaultFocusPointLon, 180, { message: 'SETTINGS_GEOCODING.LONGITUDE_INVALID' });
  });

  readonly serverErrors = new SignalFormServerErrors(
    this.formModel,
    ['defaultCountry', 'defaultFocusPointLat', 'defaultFocusPointLon'] as const,
    {
      // Backend currently emits snake_case in `data`; once it switches to RFC 9457
      // `errors[]` it may use camelCase or snake_case for the JSON Pointer — cover both.
      pointerToField: {
        default_country: 'defaultCountry',
        'defaultFocusPoint/lat': 'defaultFocusPointLat',
        'defaultFocusPoint/lon': 'defaultFocusPointLon',
        'default_focus_point/lat': 'defaultFocusPointLat',
        'default_focus_point/lon': 'defaultFocusPointLon',
      },
    }
  );

  readonly countryOptions = signal<CountryOption[]>([]);

  readonly placeSearchQuery = signal<string>('');
  readonly placeSuggestions = signal<SuggestionOption[]>([]);
  private readonly placeSearch$ = new Subject<string>();

  readonly currentFocusPoint = computed<GeocodingFocusPoint | null>(() => {
    const { defaultFocusPointLat: lat, defaultFocusPointLon: lon } = this.formModel();
    if (lat == null || lon == null) {
      return null;
    }
    return { lat, lon };
  });

  ngOnInit(): void {
    this.initializeCountryOptions();
    this.setupPlaceSearch();
    this.loadSettings();
  }

  private pickActiveLocale(): string {
    const current = this.translateService.getCurrentLang();
    if (current && current !== '') return current;
    const fallback = this.translateService.getFallbackLang();
    if (fallback && fallback !== '') return fallback;
    return 'en';
  }

  private initializeCountryOptions(): void {
    const codes = ['AT', 'DE', 'CH', 'FR', 'IT', 'ES', 'GB', 'NL', 'BE', 'SE'];
    const lang = this.pickActiveLocale();
    const displayNames = new Intl.DisplayNames([lang], { type: 'region' });
    const options = codes
      .map((code) => {
        const name = displayNames.of(code) ?? code;
        return { label: `${name} (${code})`, value: code };
      })
      .sort((a, b) => a.label.localeCompare(b.label, lang));
    this.countryOptions.set(options);
  }

  private setupPlaceSearch(): void {
    this.placeSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((query) => {
          const focusPoint = this.currentFocusPoint();
          return from(
            this.geocodingV2Api.forwardSearch({
              location: query,
              lat: focusPoint?.lat,
              lon: focusPoint?.lon,
            })
          ).pipe(catchError(() => of({ addresses: [] })));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((response) => {
        const addresses = response?.addresses ?? [];
        this.placeSuggestions.set(
          addresses
            .filter((addr) => addr.lat != null && addr.lon != null)
            .map((address) => ({
              label: this.formatSuggestionLabel(address),
              address,
            }))
        );
      });
  }

  private formatSuggestionLabel(address: SuggestedAddressV2Response): string {
    const streetLine = [address.street, address.streetNumber].filter(Boolean).join(' ');
    const cityLine = [address.postCode, address.city].filter(Boolean).join(' ');
    return [streetLine, cityLine, address.country]
      .filter((part) => part && part.length > 0)
      .join(', ');
  }

  private loadSettings(): void {
    const hadLoadedOnce = this.hasLoadedOnce();
    this.isLoading.set(true);
    this.saveError.set(null);
    from(this.geocodingSettingsApi.getSettings())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (settings) => {
          this.formModel.set({
            defaultCountry: settings.defaultCountry,
            defaultFocusPointLat: settings.defaultFocusPoint.lat,
            defaultFocusPointLon: settings.defaultFocusPoint.lon,
          });
          this.serverErrors.clear();
          this.hasLoadedOnce.set(true);
          this.initialLoadFailed.set(false);
          this.loadError.set(null);
          this.loadErrorDetails.set(null);
          this.isLoading.set(false);
        },
        error: (error: unknown) => {
          void this.handleLoadError(error, hadLoadedOnce);
        },
      });
  }

  retryLoad(): void {
    this.loadSettings();
  }

  cancel(): void {
    this.serverErrors.clear();
    this.saveError.set(null);
    this.submitted.set(false);
    this.loadSettings();
  }

  onCountryChange(country: string | null): void {
    this.saveError.set(null);
    this.formModel.update((s) => ({ ...s, defaultCountry: country ?? '' }));
  }

  onLatitudeChange(value: number | null): void {
    this.saveError.set(null);
    this.formModel.update((s) => ({ ...s, defaultFocusPointLat: value }));
  }

  onLongitudeChange(value: number | null): void {
    this.saveError.set(null);
    this.formModel.update((s) => ({ ...s, defaultFocusPointLon: value }));
  }

  onFocusPointChange(focusPoint: GeocodingFocusPoint): void {
    this.saveError.set(null);
    this.formModel.update((s) => ({
      ...s,
      defaultFocusPointLat: focusPoint.lat,
      defaultFocusPointLon: focusPoint.lon,
    }));
  }

  onPlaceSearch(event: AutoCompleteCompleteEvent): void {
    const query = (event.query ?? '').trim();
    if (query.length < 3) {
      this.placeSuggestions.set([]);
      return;
    }
    this.placeSearch$.next(query);
  }

  onPlaceSelected(event: AutoCompleteSelectEvent): void {
    const selected = event.value as SuggestionOption;
    const { lat, lon } = selected.address;
    if (lat == null || lon == null) {
      return;
    }
    this.formModel.update((s) => ({
      ...s,
      defaultFocusPointLat: lat,
      defaultFocusPointLon: lon,
    }));
    this.placeSearchQuery.set('');
    this.placeSuggestions.set([]);
  }

  saveSettings(): void {
    this.submitted.set(true);
    submit(this.settingsForm, async () => {
      const { defaultCountry, defaultFocusPointLat, defaultFocusPointLon } = this.formModel();
      if (defaultFocusPointLat == null || defaultFocusPointLon == null) {
        return;
      }

      this.isSaving.set(true);
      this.serverErrors.clear();
      this.saveError.set(null);
      const updateSettings: UpdateGeocodingSettings = {
        defaultCountry,
        defaultFocusPoint: { lat: defaultFocusPointLat, lon: defaultFocusPointLon },
      };

      try {
        const settings = await this.geocodingSettingsApi.putSettings({
          updateGeocodingSettings: updateSettings,
        });
        this.formModel.set({
          defaultCountry: settings.defaultCountry,
          defaultFocusPointLat: settings.defaultFocusPoint.lat,
          defaultFocusPointLon: settings.defaultFocusPoint.lon,
        });
      } catch (error: unknown) {
        const applied = await this.serverErrors.apply(error);
        if (applied && applied.fieldCount > 0) {
          if (applied.generalMessages.length > 0) {
            this.saveError.set(applied.generalMessages.join(' '));
          }
          return;
        }
        const joinedGeneral = applied?.generalMessages.join(' ') ?? '';
        const fallback =
          joinedGeneral !== ''
            ? joinedGeneral
            : this.translateService.instant('SETTINGS_GEOCODING.ERROR.SAVE_FAILED');
        this.saveError.set(fallback);
      } finally {
        this.isSaving.set(false);
      }
    });
  }

  clearSaveError(): void {
    this.saveError.set(null);
  }

  private async handleLoadError(error: unknown, hadLoadedOnce: boolean): Promise<void> {
    const message = this.translateService.instant('SETTINGS_GEOCODING.ERROR.LOAD_FAILED');
    if (hadLoadedOnce) {
      this.saveError.set(message);
    } else {
      this.initialLoadFailed.set(true);
      this.loadError.set(message);
      this.loadErrorDetails.set(await resolveSettingsLoadErrorDetails(error));
    }
    this.isLoading.set(false);
  }
}
